import React, { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import FanClaimButtons from "../components/FanClaimButtons";
import FanJoinHead from "../components/FanJoinHead";
import { clearAppRole, setAppRole } from "../lib/appRole";
import { FanUser, ensureFanSession, getFanUser, isFanSignedIn, needsFirstTimeScreens } from "../lib/fanAccount";
import "./FanClaimPage.css";
import { t } from "../i18n";

// App fan mode — claim screen (locked r6 §4, third phone): the one fan
// headline (FanJoinHead, 1.5.1 #15), the three sign-in doors
// (FanClaimButtons — Apple → Google → email) and a tiny "Skip for now". The skip is REQUIRED: Apple rejects apps that
// force sign-in for browsing (guideline 5.1.1). Skippers get re-asked
// naturally by the header chip on request pages — no extra prompts here.
//
// Full-screen outside Layout, same black stage as the welcome tour. Reached
// from the tour's role screen ("🎉 I'm a fan"); the magic-link round trip
// returns here too (fanEmailStart stored this path as the origin), so an
// already-signed-in mount advances silently — never a dead second ask.

const FanClaimPage: React.FC = () => {
  const navigate = useNavigate();
  // A fan coming back goes straight in, whichever door they used.
  const advance = (fan: FanUser | null) => navigate(needsFirstTimeScreens(fan) ? "/fan/name" : "/fan", { replace: true });

  // Reaching fan onboarding IS the fan pick — self-heals a device where the
  // role write didn't land (private mode) or a direct deep link.
  useEffect(() => {
    setAppRole("fan");
  }, []);

  useEffect(() => {
    let cancelled = false;
    if (isFanSignedIn()) {
      advance(getFanUser());
      return;
    }
    // Cold start with a live session (returning fan re-running onboarding,
    // magic link verified in a previous launch) — same silent advance.
    ensureFanSession().then((fan) => {
      if (!cancelled && fan) advance(fan);
    });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- once, on arrival
  }, []);

  // Back = "not a fan after all": forget the pick (Home would otherwise keep
  // booting to fan home) and reopen the role screen.
  const back = () => {
    clearAppRole();
    navigate("/welcome?role=1", { replace: true });
  };

  return (
    <div className="fanclaim-page">
      <button type="button" className="fanclaim-page-back" onClick={back} aria-label={t("fanClaimPage.back")}>
        <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <path d="M15 5l-7 7 7 7" />
        </svg>
        {t("fanClaimPage.back")}
      </button>
      <div className="fanclaim-page-inner">
        <FanJoinHead />
        <div className="fanclaim-page-btns">
          <FanClaimButtons onSignedIn={advance} />
        </div>
        <button
          type="button"
          className="fanclaim-page-skip"
          onClick={() => navigate("/fan")}
        >
          {t("fanClaimPage.skipForNow")}
        </button>
      </div>
    </div>
  );
};

export default FanClaimPage;
